import { useEffect, useRef, useState } from "react";
import {
  Sparkle, Stack, MagicWand, Palette, Images, ArrowRight, Star, Quotes,
  CaretDown, GraduationCap, PenNib, Lightning, Eye, EyeSlash,
  CopySimple, Trash, VideoCamera, Plus, Clipboard, CircleNotch, UploadSimple,
  TextB,
} from "@phosphor-icons/react";
import { mergeBuilder, parseMedia, loadFont, SECTION_DEFAULT_WIDTHS, SECTION_LABELS, newSection, rid } from "../lib/builder";
import TierPricing from "./TierPricing";

const ICONS = { Stack, MagicWand, Palette, Images, Lightning, Sparkle };

const VIBES = [
  { key: "techno", label: "Dark Techno", grad: "from-fuchsia-700 via-purple-900 to-black", copy: "Warehouse rave · magenta haze · Berlin" },
  { key: "tropical", label: "Tropical House", grad: "from-orange-400 via-pink-500 to-rose-700", copy: "Sunset rooftop · palms · golden hour" },
  { key: "luxe", label: "Luxe Gold", grad: "from-yellow-500 via-amber-700 to-black", copy: "Black-tie gala · chrome serif · champagne" },
];

const FLOAT_CSS = `@keyframes fs-floaty{0%,100%{transform:translateY(0) rotate(-2deg)}50%{transform:translateY(-16px) rotate(2deg)}}
@keyframes fs-floaty2{0%,100%{transform:translateY(0)}50%{transform:translateY(12px)}}`;

const SECTION_CLASS = {
  hero: "relative pt-24 md:pt-32 pb-20 px-6 md:px-10 overflow-hidden",
  problem: "relative px-6 md:px-10 py-20 border-t border-white/5",
  features: "relative px-6 md:px-10 py-20",
  try: "relative px-6 md:px-10 py-20 border-t border-white/5",
  academy: "relative px-6 md:px-10 py-16",
  pricing: "relative px-6 md:px-10 py-20",
  testimonials: "relative px-6 md:px-10 py-20 border-t border-white/5",
  faq: "relative px-6 md:px-10 py-20",
  finalCta: "relative px-6 md:px-10 pb-24 pt-4",
};

const MediaEmbed = ({ url, bg = false, className = "" }) => {
  const m = parseMedia(url);
  if (!m) {
    return (
      <div className={`flex items-center justify-center bg-white/5 border border-dashed border-white/15 text-zinc-500 text-sm rounded-2xl ${className}`} style={{ minHeight: 180 }}>
        <span className="inline-flex items-center gap-2"><VideoCamera size={18} /> Add a video URL</span>
      </div>
    );
  }
  if (m.kind === "video") {
    return <video src={m.src} className={className} autoPlay={bg} muted={bg} loop={bg} controls={!bg} playsInline style={bg ? { objectFit: "cover", width: "100%", height: "100%" } : {}} />;
  }
  const src = m.kind === "youtube"
    ? (bg ? `https://www.youtube.com/embed/${m.id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${m.id}&playsinline=1&modestbranding=1&showinfo=0&rel=0` : `${m.embed}?rel=0`)
    : m.kind === "vimeo"
      ? (bg ? `https://player.vimeo.com/video/${m.id}?autoplay=1&muted=1&loop=1&background=1` : m.embed)
      : m.embed || m.src;
  return (
    <iframe src={src} className={className} title="media" frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      allowFullScreen style={bg ? { pointerEvents: "none", width: "100%", height: "100%" } : {}} />
  );
};

// Stand-alone inline formatting toolbar. Kept at module scope so its own
// selection-driven re-renders never re-render the editable LandingView tree
// (which would otherwise wipe the active text selection).
function InlineFormatToolbar({ accent, onCommit }) {
  const [rt, setRt] = useState(null);
  useEffect(() => {
    const handler = () => {
      const s = window.getSelection();
      if (!s || s.isCollapsed || s.rangeCount === 0) { setRt(null); return; }
      const range = s.getRangeAt(0);
      let node = range.commonAncestorContainer;
      if (node.nodeType === 3) node = node.parentElement;
      const host = node && node.closest ? node.closest("[data-edit-path]") : null;
      if (!host) { setRt(null); return; }
      const r = range.getBoundingClientRect();
      setRt({ x: r.left + r.width / 2, y: r.top, path: host.getAttribute("data-edit-path"), node: host });
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);
  const apply = (cmd, val) => {
    try { document.execCommand("styleWithCSS", false, true); } catch (e) { /* noop */ }
    document.execCommand(cmd, false, val);
    if (rt && rt.node) onCommit(rt.path, rt.node.innerHTML);
  };
  if (!rt) return null;
  return (
    <div data-testid="inline-rt-toolbar" className="fixed z-[60] flex items-center gap-1.5 rounded-full bg-[#111] border border-white/15 shadow-2xl px-2 py-1.5" style={{ left: rt.x, top: Math.max(8, rt.y - 48), transform: "translateX(-50%)" }} onMouseDown={(e) => e.preventDefault()}>
      <button title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => apply("bold")} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white"><TextB size={15} weight="bold" /></button>
      <span className="w-px h-5 bg-white/15" />
      {["#FFD60A", "#ffffff", "#ff4d4d", "#4dd0ff", "#7CFC00", "#ff7ac0"].map((col) => (
        <button key={col} title={col} onMouseDown={(e) => e.preventDefault()} onClick={() => apply("foreColor", col)} className="h-5 w-5 rounded-full border border-white/25" style={{ backgroundColor: col }} />
      ))}
      <label className="h-6 w-6 rounded-full overflow-hidden border border-white/25 cursor-pointer relative" title="Custom color">
        <input type="color" onMouseDown={(e) => e.preventDefault()} onChange={(e) => apply("foreColor", e.target.value)} className="absolute -inset-2 w-10 h-10 cursor-pointer" />
        <Palette size={13} className="absolute inset-0 m-auto text-white pointer-events-none" />
      </label>
    </div>
  );
}

export default function LandingView({
  content,
  editable = false,
  onChange = () => {},
  onContent = () => {},
  onNavigate = () => {},
  onUpload = async () => "",
  onCopy = () => {},
  sel = {},
  onSelectText = () => {},
  onSelectSection = () => {},
  onToggleHidden = () => {},
  onChoosePlan = () => {},
  onTopup = () => {},
}) {
  const [faqOpen, setFaqOpen] = useState(0);
  const [vibe, setVibe] = useState(0);
  const c = content;
  const scrollToId = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const B = mergeBuilder(c);
  const accent = c.accent || "#FFD60A";
  const get = (path) => path.reduce((a, k) => (a == null ? a : a[k]), c);
  const pstr = (path) => path.join(".");

  const dispFam = c.fontDisplay && c.fontDisplay !== "Outfit" ? `'${c.fontDisplay}', sans-serif` : undefined;
  const bodyFam = B.fontBody && B.fontBody !== "Outfit" ? `'${B.fontBody}', sans-serif` : undefined;
  const headStyle = dispFam ? { fontFamily: dispFam } : {};

  useEffect(() => {
    const fams = new Set([c.fontDisplay, B.fontBody]);
    Object.values(B.textStyles || {}).forEach((s) => s && s.fontFamily && fams.add(s.fontFamily));
    fams.forEach((f) => loadFont(f));
  }, [c.fontDisplay, B.fontBody, B.textStyles]);

  const sw = (k) => B.sectionWidths[k] ?? SECTION_DEFAULT_WIDTHS[k] ?? 1280;
  const hidden = (k) => (B.hiddenSections || []).includes(k);
  const tStyle = (path) => {
    const s = (B.textStyles || {})[pstr(path)];
    if (!s) return {};
    const o = {};
    if (s.fontFamily) o.fontFamily = `'${s.fontFamily}', sans-serif`;
    if (s.fontSize) o.fontSize = s.fontSize;
    if (s.fontWeight) o.fontWeight = s.fontWeight;
    if (s.italic) o.fontStyle = "italic";
    if (s.align) o.textAlign = s.align;
    if (s.color) o.color = s.color;
    if (s.gradient && s.gradient.start && s.gradient.end) {
      o.backgroundImage = `linear-gradient(${s.gradient.angle ?? 90}deg, ${s.gradient.start}, ${s.gradient.end})`;
      o.WebkitBackgroundClip = "text"; o.backgroundClip = "text";
      o.color = "transparent"; o.WebkitTextFillColor = "transparent";
    }
    return o;
  };

  // ---- structural mutations ----
  const dupItem = (arrPath, i) => onContent((d) => { const a = arrPath.reduce((x, k) => x[k], d); a.splice(i + 1, 0, structuredClone(a[i])); return d; });
  const delItem = (arrPath, i, min = 1) => onContent((d) => { const a = arrPath.reduce((x, k) => x[k], d); if (a.length > min) a.splice(i, 1); return d; });
  const addBelow = (entryId) => onContent((d) => {
    const ns = newSection("text"); d.builder.extraSections.push(ns);
    const i = d.builder.sectionOrder.findIndex((e) => e.id === entryId);
    d.builder.sectionOrder.splice(i + 1, 0, { id: rid("ord"), type: "custom", sid: ns.id });
    return d;
  });
  const dupEntry = (entry) => onContent((d) => {
    const i = d.builder.sectionOrder.findIndex((e) => e.id === entry.id);
    if (entry.type === "custom") {
      const src = d.builder.extraSections.find((s) => s.id === entry.sid);
      const cp = structuredClone(src); cp.id = rid("sec");
      d.builder.extraSections.push(cp);
      d.builder.sectionOrder.splice(i + 1, 0, { id: rid("ord"), type: "custom", sid: cp.id });
    } else {
      d.builder.sectionOrder.splice(i + 1, 0, { id: rid("ord"), type: "curated", key: entry.key });
    }
    return d;
  });
  const delEntry = (entry) => onContent((d) => {
    d.builder.sectionOrder = d.builder.sectionOrder.filter((e) => e.id !== entry.id);
    if (entry.type === "custom") {
      const stillRef = d.builder.sectionOrder.some((e) => e.type === "custom" && e.sid === entry.sid);
      if (!stillRef) d.builder.extraSections = d.builder.extraSections.filter((s) => s.id !== entry.sid);
    }
    return d;
  });
  const copyEntry = (entry) => {
    if (entry.type === "custom") { const src = B.extraSections.find((s) => s.id === entry.sid); onCopy({ kind: "custom", obj: structuredClone(src) }); }
    else onCopy({ kind: "curated", key: entry.key });
  };

  // ---- inline editable primitives ----
  const T = ({ path, as: Tag = "span", className = "", style = {} }) => {
    const value = get(path) ?? "";
    const merged = { ...style, ...tStyle(path) };
    const html = { __html: String(value) };
    if (!editable) return <Tag className={className} style={merged} dangerouslySetInnerHTML={html} />;
    const selected = sel.path === pstr(path);
    return (
      <Tag
        data-edit-path={pstr(path)}
        contentEditable
        suppressContentEditableWarning
        onClick={(e) => { e.stopPropagation(); onSelectText(pstr(path)); }}
        onBlur={(e) => onChange(path, e.currentTarget.innerHTML)}
        className={`${className} outline-none rounded transition cursor-text`}
        style={{ ...merged, boxShadow: selected ? `0 0 0 2px ${accent}` : undefined }}
        dangerouslySetInnerHTML={html}
      />
    );
  };

  const UploadBtn = ({ accept, onDone, label, className }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    return (
      <>
        <button type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); ref.current?.click(); }} className={className}>
          {busy ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />} {busy ? "Uploading…" : label}
        </button>
        <input ref={ref} type="file" accept={accept} className="hidden"
          onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { const url = await onUpload(f); if (url) onDone(url); } catch (err) { window.alert("Upload failed. Please try again."); } finally { setBusy(false); e.target.value = ""; } }} />
      </>
    );
  };

  const Img = ({ path, className = "", alt = "", style = {} }) => {
    const src = get(path);
    if (!editable) return <img src={src} alt={alt} className={className} style={style} />;
    return (
      <div className="relative group inline-block w-full">
        <img src={src} alt={alt} className={className} style={style} />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 opacity-0 group-hover:opacity-100 transition rounded-xl" onClick={(e) => e.stopPropagation()}>
          <UploadBtn accept="image/*" label="Upload image" onDone={(url) => onChange(path, url)} className="px-3 py-1.5 rounded-full bg-[#FFD60A] text-black text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" />
          <button onClick={() => { const u = window.prompt("Image URL:", src); if (u !== null) onChange(path, u); }} className="text-[11px] text-zinc-200 underline">or paste URL</button>
        </div>
      </div>
    );
  };

  const ItemTools = ({ arrPath, i, min = 1 }) => {
    if (!editable) return null;
    return (
      <div className="absolute -top-2.5 -right-2.5 z-30 flex gap-1" onClick={(e) => e.stopPropagation()}>
        <button data-testid={`dup-${arrPath.join("-")}-${i}`} title="Duplicate" onClick={() => dupItem(arrPath, i)} className="h-6 w-6 rounded-full text-black flex items-center justify-center shadow" style={{ backgroundColor: accent }}><CopySimple size={12} weight="bold" /></button>
        <button data-testid={`del-${arrPath.join("-")}-${i}`} title="Delete" onClick={() => delItem(arrPath, i, min)} className="h-6 w-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow"><Trash size={12} weight="bold" /></button>
      </div>
    );
  };

  // Section toolbar: Add / Duplicate / Copy / Delete (exactly four).
  const SectionToolbar = ({ entry }) => (
    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 rounded-full bg-[#111] border border-white/15 shadow-xl px-1 py-1 opacity-30 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`section-add-${entry.id}`} title="Add blank section below" onClick={() => addBelow(entry.id)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-200"><Plus size={14} weight="bold" /></button>
      <button data-testid={`section-dup-${entry.id}`} title="Duplicate section" onClick={() => dupEntry(entry)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center" style={{ color: accent }}><CopySimple size={14} weight="bold" /></button>
      <button data-testid={`section-copy-${entry.id}`} title="Copy to clipboard" onClick={() => copyEntry(entry)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-200"><Clipboard size={14} weight="bold" /></button>
      <button data-testid={`section-del-${entry.id}`} title="Delete section" onClick={() => delEntry(entry)} className="h-7 w-7 rounded-full hover:bg-rose-600/30 flex items-center justify-center text-rose-300"><Trash size={14} weight="bold" /></button>
    </div>
  );

  // ---- curated section bodies ----
  const heroVid = B.heroVideo;
  const hasHeroVid = heroVid?.show && parseMedia(heroVid.url);

  const renderCurated = (key) => {
    switch (key) {
      case "hero":
        return (
          <>
            {hasHeroVid && (<div className="absolute inset-0 z-0"><MediaEmbed url={heroVid.url} bg className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-black/70" /></div>)}
            <div className="absolute -top-20 -left-32 h-[480px] w-[480px] rounded-full bg-fuchsia-600/15 blur-[130px] pointer-events-none" />
            <div className="absolute top-32 right-0 h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[130px] pointer-events-none" />
            <div className="mx-auto grid lg:grid-cols-12 gap-12 items-center relative z-10" style={{ maxWidth: sw("hero") }}>
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs text-zinc-300 mb-6">
                  <Sparkle weight="fill" size={14} style={{ color: accent }} /> <T path={["hero", "badge"]} />
                </div>
                <h1 className="font-display font-black text-5xl md:text-7xl xl:text-8xl tracking-tighter leading-[0.9] mb-6 uppercase" style={headStyle}>
                  <T path={["hero", "titleTop"]} /><br />
                  <T path={["hero", "titleAccent"]} className="bg-gradient-to-r from-orange-400 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent" />
                </h1>
                <T path={["hero", "subtitle"]} as="p" className="text-lg text-zinc-400 max-w-xl mb-9 leading-relaxed block" />
                <div className="flex flex-wrap items-center gap-4">
                  <button onClick={() => onNavigate("/ai-pro-studio")} data-testid="hero-cta-primary" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold transition-transform hover:scale-105" style={{ backgroundColor: accent, color: "#0a0a0a" }}><T path={["hero", "ctaPrimary"]} /><ArrowRight weight="bold" size={18} /></button>
                  <button onClick={() => onNavigate("/ai-pro-studio")} data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5 font-semibold transition-colors"><T path={["hero", "ctaSecondary"]} /></button>
                </div>
                <div className="flex items-center gap-4 mt-7 text-sm text-zinc-400">
                  <div className="flex items-center gap-1" style={{ color: accent }}>{[0, 1, 2, 3, 4].map((i) => <Star key={i} weight="fill" size={15} />)}</div>
                  <T path={["hero", "rating"]} />
                </div>
              </div>
              <div className="lg:col-span-5 relative" style={{ animation: "fs-floaty 7s ease-in-out infinite" }}>
                <Img path={["hero", "image"]} alt="Flyer preview" className="rounded-2xl border border-white/10 shadow-2xl shadow-black/70 w-full object-cover aspect-[3/4]" />
                <div className="absolute -bottom-5 -left-5 bg-[#121212] border border-white/10 rounded-xl px-4 py-3 shadow-xl flex items-center gap-3" style={{ animation: "fs-floaty2 5s ease-in-out infinite" }}>
                  <MagicWand weight="fill" size={22} style={{ color: accent }} />
                  <div><p className="text-xs text-zinc-400">Prompt → Poster</p><p className="text-sm font-semibold">Generated in seconds</p></div>
                </div>
              </div>
            </div>
            <div className="mx-auto mt-16 flex flex-wrap justify-center gap-3 relative z-10" style={{ maxWidth: sw("hero") }}>
              {(c.marquee || []).map((m, i) => <span key={i} className="px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] text-xs font-medium text-zinc-400">{m}</span>)}
            </div>
          </>
        );
      case "problem":
        return (
          <div className="mx-auto grid lg:grid-cols-2 gap-12 items-center" style={{ maxWidth: sw("problem") }}>
            <div>
              <T path={["problem", "eyebrow"]} className="text-sm font-bold uppercase tracking-wider" style={{ color: accent }} />
              <T path={["problem", "title"]} as="h2" className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-3 mb-4 block" style={headStyle} />
              <T path={["problem", "subtitle"]} as="p" className="text-zinc-400 text-lg leading-relaxed block" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 grayscale opacity-70"><Img path={["problem", "beforeImage"]} alt="before" className="w-full object-cover aspect-[3/4]" /><span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-black/70 px-2 py-1 rounded z-10">Before</span></div>
              <div className="relative rounded-2xl overflow-hidden border-2" style={{ borderColor: accent }}><Img path={["problem", "afterImage"]} alt="after" className="w-full object-cover aspect-[3/4]" /><span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-black z-10" style={{ backgroundColor: accent }}>After</span></div>
            </div>
          </div>
        );
      case "features":
        return (
          <div className="mx-auto" style={{ maxWidth: sw("features") }}>
            <div className="max-w-2xl mb-12"><h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter mb-3" style={headStyle}>Everything you need to look like a pro.</h2><p className="text-zinc-400 text-lg">Four tools that turn a blank canvas into a sold-out event.</p></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {(c.features || []).map((f, i) => { const Ico = ICONS[f.icon] || Stack; return (
                <div key={i} className="relative rounded-2xl border border-white/10 bg-[#0E0E0E] p-6 hover:bg-[#141414] hover:-translate-y-1 transition-all">
                  <ItemTools arrPath={["features"]} i={i} />
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}40` }}><Ico weight="fill" size={22} style={{ color: accent }} /></div>
                  <T path={["features", i, "tag"]} className="text-[11px] font-bold uppercase tracking-wider text-zinc-500" />
                  <T path={["features", i, "title"]} as="h3" className="font-display text-lg font-bold tracking-tight mt-1 mb-2 block" />
                  <T path={["features", i, "desc"]} as="p" className="text-sm text-zinc-400 leading-relaxed block" />
                </div>); })}
            </div>
          </div>
        );
      case "try":
        return (
          <div className="mx-auto text-center" style={{ maxWidth: sw("try") }}>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter mb-3" style={headStyle}>One prompt. Three vibes.</h2>
            <p className="text-zinc-400 mb-8">Tap a style and watch the mood change instantly.</p>
            <div className="flex flex-wrap justify-center gap-3 mb-10">{VIBES.map((v, i) => <button key={v.key} data-testid={`vibe-${v.key}`} onClick={(e) => { e.stopPropagation(); setVibe(i); }} className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${vibe === i ? "text-black" : "border border-white/15 text-zinc-300 hover:bg-white/5"}`} style={vibe === i ? { backgroundColor: accent } : {}}>{v.label}</button>)}</div>
            <div className={`mx-auto max-w-md aspect-[4/5] rounded-3xl border border-white/10 bg-gradient-to-br ${VIBES[vibe].grad} flex flex-col items-center justify-center p-8 shadow-2xl transition-all duration-500`}>
              <span className="text-xs uppercase tracking-[0.3em] text-white/70 mb-3">Live preview</span>
              <h3 className="font-display font-black text-4xl uppercase tracking-tighter text-white text-center drop-shadow-lg">{VIBES[vibe].label}</h3>
              <p className="text-white/80 text-sm mt-3 text-center">{VIBES[vibe].copy}</p>
              <button onClick={() => onNavigate("/ai-pro-studio")} className="mt-6 px-5 py-2.5 rounded-full bg-black/40 backdrop-blur border border-white/20 text-white text-sm font-semibold hover:bg-black/60 transition-colors">Make this flyer →</button>
            </div>
          </div>
        );
      case "academy":
        return (
          <div className="mx-auto rounded-3xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0a0a0a] p-10 md:p-14 flex flex-col md:flex-row items-center gap-8" style={{ maxWidth: sw("academy") }}>
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}40` }}><GraduationCap weight="fill" size={32} style={{ color: accent }} /></div>
            <div className="flex-1 text-center md:text-left">
              <T path={["academy", "eyebrow"]} className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }} />
              <T path={["academy", "title"]} as="h3" className="font-display font-black text-2xl md:text-3xl tracking-tight mt-1 mb-2 block" style={headStyle} />
              <T path={["academy", "subtitle"]} as="p" className="text-zinc-400 block" />
            </div>
            <button onClick={() => onNavigate("/ai-pro-studio")} className="px-6 py-3 rounded-full font-bold whitespace-nowrap transition-transform hover:scale-105" style={{ backgroundColor: accent, color: "#0a0a0a" }}><T path={["academy", "cta"]} /></button>
          </div>
        );
      case "pricing":
        return (
          <div className="mx-auto" style={{ maxWidth: sw("pricing") }}>
            <div className="text-center mb-12">
              <T path={["pricingHeading", "eyebrow"]} className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }} />
              <T path={["pricingHeading", "title"]} as="h2" className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-3 mb-3 block" style={headStyle} />
              <T path={["pricingHeading", "subtitle"]} as="p" className="text-zinc-400 max-w-xl mx-auto block" />
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <TierPricing onChoose={onChoosePlan} onTopup={onTopup} showTopups />
            </div>
          </div>
        );
      case "testimonials":
        return (
          <div className="mx-auto" style={{ maxWidth: sw("testimonials") }}>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-center mb-12" style={headStyle}>Promoters who stopped hiring designers.</h2>
            <div className="grid md:grid-cols-3 gap-6">{(c.testimonials || []).map((t, i) => (
              <div key={i} className="relative rounded-2xl border border-white/10 bg-[#0E0E0E] p-7">
                <ItemTools arrPath={["testimonials"]} i={i} />
                <Quotes weight="fill" size={28} style={{ color: accent }} className="mb-4" />
                <T path={["testimonials", i, "quote"]} as="p" className="text-zinc-200 leading-relaxed mb-6 block" />
                <div className="flex items-center gap-3"><img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover" /><div><T path={["testimonials", i, "name"]} as="p" className="font-semibold text-sm block" /><T path={["testimonials", i, "role"]} as="p" className="text-xs text-zinc-500 block" /></div></div>
              </div>))}
            </div>
          </div>
        );
      case "faq":
        return (
          <div className="mx-auto" style={{ maxWidth: sw("faq") }}>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-center mb-12" style={headStyle}>Questions, answered.</h2>
            <div className="space-y-3">{(c.faq || []).map((item, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="relative rounded-2xl border border-white/10 bg-[#0E0E0E] overflow-hidden">
                <ItemTools arrPath={["faq"]} i={i} />
                <button onClick={(e) => { e.stopPropagation(); setFaqOpen(faqOpen === i ? -1 : i); }} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"><T path={["faq", i, "q"]} className="font-semibold" /><CaretDown size={18} className={`shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} style={{ color: accent }} /></button>
                {faqOpen === i && <T path={["faq", i, "a"]} as="p" className="px-6 pb-5 text-zinc-400 leading-relaxed text-sm block" />}
              </div>))}
            </div>
          </div>
        );
      case "finalCta":
        return (
          <div className="mx-auto rounded-3xl p-12 md:p-16 text-center relative overflow-hidden" style={{ maxWidth: sw("finalCta"), background: `radial-gradient(120% 120% at 50% 0%, ${accent}22 0%, #0a0a0a 60%)`, border: "1px solid rgba(255,255,255,0.1)" }}>
            <T path={["finalCta", "title"]} as="h2" className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-4 uppercase block" style={headStyle} />
            <T path={["finalCta", "subtitle"]} as="p" className="text-zinc-400 mb-8 max-w-xl mx-auto block" />
            <button onClick={() => onNavigate("/ai-pro-studio")} data-testid="final-cta-btn" className="inline-flex items-center gap-2 px-9 py-4 rounded-full font-bold transition-transform hover:scale-105" style={{ backgroundColor: accent, color: "#0a0a0a" }}><T path={["finalCta", "cta"]} /> <ArrowRight weight="bold" size={18} /></button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderCustom = (obj) => {
    const idx = B.extraSections.findIndex((s) => s.id === obj.id);
    if (obj.type === "media") {
      return (
        <div className="mx-auto" style={{ maxWidth: obj.width ?? 1024 }}>
          <div className="rounded-2xl overflow-hidden aspect-video bg-black relative">
            <MediaEmbed url={obj.url} className="absolute inset-0 w-full h-full" />
            {editable && (
              <div className="absolute top-2 left-2 z-20 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <UploadBtn accept="video/*" label="Upload MP4" onDone={(url) => onChange(["builder", "extraSections", idx, "url"], url)} className="px-3 py-1.5 rounded-full bg-[#FFD60A] text-black text-xs font-bold inline-flex items-center gap-1.5" />
                <button onClick={() => { const u = window.prompt("Video URL (YouTube / Vimeo / Facebook / MP4):", obj.url || ""); if (u !== null) onChange(["builder", "extraSections", idx, "url"], u); }} className="px-3 py-1.5 rounded-full bg-black/70 border border-white/15 text-xs font-bold inline-flex items-center gap-1.5"><VideoCamera size={13} /> URL</button>
              </div>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="mx-auto" style={{ maxWidth: obj.width ?? 768, textAlign: obj.align || "center" }}>
        <T path={["builder", "extraSections", idx, "heading"]} as="h2" className="font-display font-black text-3xl md:text-5xl tracking-tighter mb-4 block" style={headStyle} />
        <T path={["builder", "extraSections", idx, "body"]} as="p" className="text-zinc-400 text-lg leading-relaxed block" />
      </div>
    );
  };

  const SectionShell = ({ entry }) => {
    const isCustom = entry.type === "custom";
    const obj = isCustom ? B.extraSections.find((s) => s.id === entry.sid) : null;
    if (isCustom && !obj) return null;
    const key = isCustom ? null : entry.key;
    const isHidden = !isCustom && hidden(key);
    if (isHidden && !editable) return null;
    const selected = editable && sel.section === entry.id;
    const style = {};
    if (isCustom) { style.paddingTop = obj.paddingY ?? 64; style.paddingBottom = obj.paddingY ?? 64; if (obj.bg) style.backgroundColor = obj.bg; }
    else { const s = B.sectionStyles?.[key] || {}; if (s.paddingY != null) { style.paddingTop = s.paddingY; style.paddingBottom = s.paddingY; } if (s.bg) style.backgroundColor = s.bg; }
    if (selected) style.boxShadow = `inset 0 0 0 2px ${accent}`;
    const cls = isCustom ? "relative px-6 md:px-10" : (SECTION_CLASS[key] || "relative px-6 md:px-10 py-16");
    return (
      <section data-section={entry.id} id={!isCustom ? key : undefined} {...(editable ? { onClick: () => onSelectSection(entry.id) } : {})} className={`group scroll-mt-20 ${cls} ${isHidden && editable ? "opacity-40" : ""}`} style={style}>
        {editable && <SectionToolbar entry={entry} />}
        {editable && (
          <div className="absolute top-1.5 right-1.5 z-30 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/75 border border-white/15 text-zinc-200">{isCustom ? (obj.type === "media" ? "Video" : "Text") : (SECTION_LABELS[key] || key)}</span>
            {!isCustom && <button data-testid={`sec-hide-${key}`} title={isHidden ? "Show" : "Hide"} onClick={() => onToggleHidden(key)} className="h-6 w-6 rounded-full bg-black/75 border border-white/15 flex items-center justify-center text-zinc-200 hover:text-white">{isHidden ? <EyeSlash size={12} /> : <Eye size={12} />}</button>}
          </div>
        )}
        {isCustom ? renderCustom(obj) : renderCurated(key)}
      </section>
    );
  };

  const FixedSec = ({ skey, className = "", children }) => {
    if (hidden(skey) && !editable) return null;
    return (
      <section data-section={skey} {...(editable ? { onClick: () => onSelectSection(skey) } : {})} className={`relative ${className} ${hidden(skey) && editable ? "opacity-40" : ""}`} style={editable && sel.section === skey ? { boxShadow: `inset 0 0 0 2px ${accent}` } : {}}>
        {editable && (
          <div className="absolute top-1.5 right-1.5 z-30 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/75 border border-white/15 text-zinc-200">{SECTION_LABELS[skey]}</span>
            <button data-testid={`sec-hide-${skey}`} title={hidden(skey) ? "Show" : "Hide"} onClick={() => onToggleHidden(skey)} className="h-6 w-6 rounded-full bg-black/75 border border-white/15 flex items-center justify-center text-zinc-200 hover:text-white">{hidden(skey) ? <EyeSlash size={12} /> : <Eye size={12} />}</button>
          </div>
        )}
        {children}
      </section>
    );
  };

  return (
    <div className="bg-[#070707] text-white overflow-x-hidden font-body selection:bg-[#FFD60A] selection:text-black" style={{ maxWidth: B.canvasWidth, margin: "0 auto", ...(bodyFam ? { fontFamily: bodyFam } : {}) }}>
      <style>{FLOAT_CSS}</style>

      {/* NAV (fixed) */}
      <FixedSec skey="nav" className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="mx-auto px-6 md:px-10 h-16 flex items-center justify-between" style={{ maxWidth: sw("nav") }}>
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent }}><PenNib weight="fill" size={18} className="text-black" /></div>
            <T path={["brand"]} as="span" className="font-display font-extrabold text-lg tracking-tight" style={headStyle} />
          </div>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-zinc-300">
            <button onClick={() => scrollToId("features")} className="hover:text-white transition-colors">Features</button>
            <button onClick={() => scrollToId("try")} className="hover:text-white transition-colors">Demo</button>
            <button onClick={() => scrollToId("pricing")} className="hover:text-white transition-colors">Pricing</button>
            <button onClick={() => scrollToId("faq")} className="hover:text-white transition-colors">FAQ</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onNavigate("/login")} data-testid="nav-login-btn" className="text-sm font-semibold text-zinc-200 hover:text-white px-3 py-2 transition-colors">Log in</button>
            <button onClick={() => onNavigate("/ai-pro-studio")} data-testid="nav-signup-btn" className="text-sm font-bold px-4 py-2 rounded-full transition-transform hover:scale-105" style={{ backgroundColor: accent, color: "#0a0a0a" }}>Start free</button>
          </div>
        </div>
      </FixedSec>

      {/* ORDERED SECTIONS */}
      {B.sectionOrder.map((entry) => <SectionShell key={entry.id} entry={entry} />)}

      {/* FOOTER (fixed) */}
      <FixedSec skey="footer" className="border-t border-white/10 py-8 px-6 md:px-10">
        <div className="mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-zinc-600" style={{ maxWidth: sw("footer") }}>
          <span>© 2026 {c.brand}. Club-ready flyers in one prompt.</span>
          <div className="flex gap-5"><span className="hover:text-zinc-300">Pricing</span><button onClick={() => onNavigate("/login")} className="hover:text-zinc-300">Log in</button></div>
        </div>
      </FixedSec>

      {/* INLINE RICH-TEXT TOOLBAR */}
      {editable && <InlineFormatToolbar accent={accent} onCommit={(pathStr, html) => onChange(pathStr.split(".").map((k) => (/^\d+$/.test(k) ? Number(k) : k)), html)} />}
    </div>
  );
}
